<script>
export default {
  inheritAttrs: false,
}
</script>

<script setup>
// Imports
import CodeEditor from 'components/UI/CodeEditor.vue'
import ColorPicker from 'components/UI/ColorPicker.vue'
import DatePicker from 'components/UI/DatePicker.vue'
import FileUploader from 'components/UI/FileUploader.vue'
import LabelSelect from 'components/UI/LabelSelect.vue'
import OmniJsonEditor from 'components/UI/OmniJsonEditor.vue'
import OmniSelect from 'components/UI/OmniSelect.vue'
import OmniSlider from 'components/UI/OmniSlider.vue'
import RichTextEditor from 'components/UI/RichTextEditor.vue'
import { phone } from 'phone'
import { format } from 'quasar'
import { getErrorMessage, getFieldType, getOptions } from 'src/utils/utils'
import { useUserStore } from 'stores/userStore'

const { capitalize } = format
const { locale, t, te } = useI18n({ useScope: 'global' })
const attrs = useAttrs()
const $q = useQuasar()
const emit = defineEmits(['update:modelValue', 'update:tabError', 'fieldError', 'fieldNoError'])
const props = defineProps({
  schema: {
    type: undefined,
    required: false,
    default: undefined,
  },
  modelValue: {
    type: undefined,
    required: false,
    default: undefined,
  },
  formErrors: {
    type: undefined,
    required: false,
    default: undefined,
  },
  parentHasError: {
    type: undefined,
    required: false,
    default: undefined,
  },
  itemsService: {
    type: undefined,
    required: false,
    default: undefined,
  },
  itemKey: {
    type: undefined,
    required: false,
    default: undefined,
  },
  tabError: {
    type: undefined,
    required: false,
    default: undefined,
  },
  optionsConfig: {
    type: undefined,
    required: false,
    default: undefined,
  },
  requireDependency: {
    type: undefined,
    required: false,
    default: undefined,
  },
  country: {
    type: undefined,
    required: false,
    default: undefined,
  },
  countries: {
    type: Array,
    required: false,
    default: undefined,
  },
})

const formElement = ref(null)
const suggestionMenu = ref(false)
const protectedSecretInputValue = ref('')
const protectedSecretStoredValue = ref()
const protectedSecretIsEditing = ref(false)

const filteredSuggestions = computed(() => {
  const term = props.modelValue?.trim().toLowerCase()
  if (!term) {
    return props.schema.suggestions ?? null
  }

  return props.schema.suggestions?.filter((option) => option.toLowerCase().includes(term)) ?? null
})

const openSuggestionMenu = () => {
  if (props.schema.suggestions && filteredSuggestions.value.length > 0) {
    suggestionMenu.value = true
  }
}

const selectSuggestion = (suggestion) => {
  emit('update:modelValue', suggestion)
  suggestionMenu.value = false
}

const translationString = computed(() => {
  let prefix = process.env.FRONTEND_TYPE + '.' + capitalize(props.itemsService)
  if (props.schema.translationPrefix) {
    prefix = props.schema.translationPrefix
  } else if (props.itemsService === 'users') {
    prefix = 'Shared.Users'
  }

  return prefix
})

const emitValue = (value) => {
  emit('update:modelValue', value)
}

const protectedSecretPlaceholder = computed(() => {
  if (protectedSecretStoredValue.value !== null && typeof protectedSecretStoredValue.value !== 'undefined' && protectedSecretStoredValue.value !== '') {
    return protectedSecretStoredValue.value
  }

  return props.schema.placeholder
})

const emitProtectedSecretValue = (value) => {
  if (value === '' || value === null) {
    protectedSecretInputValue.value = ''
    protectedSecretIsEditing.value = false
    emit('update:modelValue', protectedSecretStoredValue.value)
    return
  }

  protectedSecretInputValue.value = value
  protectedSecretIsEditing.value = true
  emit('update:modelValue', value)
}

const onModelValueChange = (value) => {
  if (value === '' || value === null || isNaN(Number(value))) {
    emit('update:modelValue', null)
  } else {
    emit('update:modelValue', parseFloat(value))
  }
}

const isClearableField = computed(() => {
  const clearable = typeof props.schema.clearable !== 'undefined' ? props.schema.clearable : true
  if (typeof attrs.required !== 'undefined') {
    if (attrs.required) {
      return clearable && attrs.required
    }
  } else {
    if (!props.schema.required && props.schema.clearable) {
      return clearable
    }
    return props.schema.required && clearable
  }
})

const isRequiredField = computed(() => {
  if (typeof attrs.required !== 'undefined') {
    if (attrs.required) {
      return attrs.required
    }
  } else {
    return props.schema.required
  }
})

const phoneCountry = ref('')
watch(
  () => props.modelValue,
  (value) => {
    if (props.schema.type !== 'protected-secret') {
      return
    }

    if (protectedSecretIsEditing.value && value === protectedSecretInputValue.value) {
      return
    }

    protectedSecretStoredValue.value = value
    protectedSecretInputValue.value = ''
    protectedSecretIsEditing.value = false
  },
  {
    immediate: true,
  },
)
watch(
  () => props.country,
  () => {
    if (props.country) {
      phoneCountry.value = props.country
    }
  },
  {
    immediate: true,
  },
)
const emitPhoneValue = (value) => {
  const validPhone = phone(value, {
    country: phoneCountry.value,
    validateMobilePrefix: false,
  })
  if (phoneCountry.value && validPhone.isValid) {
    emit('update:modelValue', validPhone.phoneNumber)
  } else {
    emit('update:modelValue', value)
  }
}

const emitValueAsInteger = (value) => {
  emit('update:modelValue', +value)
}

const showPlaceholder = computed(() => {
  return (
    props.schema.showPlaceholder ??
    ((!props.schema.searchable || props.schema.placeholder) && (!props.modelValue || props.modelValue === '' || props.modelValue.length < 1))
  )
})

const showLabel = computed(() => {
  return (
    !props.optionsConfig?.avatar ||
    (props.schema.searchable && !props.schema.placeholder && (!props.modelValue || props.modelValue === '' || props.modelValue.length < 1))
  )
})

const stars = computed(() => [1, 2, 3, 4, 5])
const hoverRating = ref(null)
const effectiveRating = computed(() => {
  const hovered = hoverRating.value
  if (typeof hovered === 'number') {
    return hovered
  }
  const value = typeof props.modelValue !== 'undefined' ? props.modelValue : props.schema.value
  return typeof value === 'number' ? value : 0
})

const setHoverRating = (value) => {
  hoverRating.value = value
}

const clearHoverRating = () => {
  hoverRating.value = null
}

const setRating = (value) => {
  emitValue(value)
}

const phoneConfig = computed(() => {
  return {
    field: 'country',
    valueKey: 'alpha2',
    labelKey: 'country',
    translateKeyBase: 'Shared.Globals.Countries',
    translateKey: 'alpha3',
    avatar: true,
    avatarValuePrefix: 'fi fi-',
    avatarValue: 'alpha2',
  }
})

watchEffect(() => {
  if (formElement?.value?.hasError) {
    emit('fieldError', props.itemKey)
  } else {
    emit('fieldNoError', props.itemKey)
  }
})

onMounted(() => {})

defineExpose({
  get child() {
    return formElement.value
  },
})
</script>

<template>
  <div
    v-show="!props.schema.isHidden"
    id="field-generator"
    :style="!$attrs['field-class'] ? 'display: contents' : undefined"
    :class="{ [$attrs['field-class']]: !!$attrs['field-class'] }"
  >
    <q-input
      v-if="schema.type === 'string'"
      ref="formElement"
      :lang="useUserStore().currentRegionalSettings.lang"
      :outlined="schema.outlined ?? true"
      :dense="schema.dense ?? false"
      :prefix="props.schema.prefix"
      :maxlength="schema.maxLength"
      :readonly="schema.readonly"
      :type="schema.inputType || getFieldType(schema.type)"
      :autocomplete="schema.autocomplete"
      :model-value="typeof props.modelValue !== 'undefined' ? props.modelValue : props.schema.value"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : t(translationString + '.' + capitalize(props.itemKey))) +
            (isRequiredField ? ' *' : '')
      "
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      v-bind="$attrs"
      @update:model-value="emitValue"
      @focus="props.schema.suggestions ? openSuggestionMenu : undefined"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>

      <q-menu v-if="schema.suggestions" v-model="suggestionMenu" anchor="bottom left" self="top left" fit no-focus>
        <q-list dense>
          <q-item v-for="option in filteredSuggestions" :key="option" clickable @click="selectSuggestion(option)">
            <q-item-section>{{ option }}</q-item-section>
          </q-item>

          <q-item v-if="!filteredSuggestions.length" dense>
            <q-item-section class="text-grey">{{ $t('Shared.Globals.No suggestions') }}</q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-input>

    <q-input
      v-if="schema.type === 'protected-secret'"
      ref="formElement"
      :lang="useUserStore().currentRegionalSettings.lang"
      :outlined="schema.outlined ?? true"
      :dense="schema.dense ?? false"
      :maxlength="schema.maxLength"
      :readonly="schema.readonly"
      :hide-bottom-space="schema.multiline ? true : undefined"
      :type="schema.multiline ? 'textarea' : 'text'"
      :rows="schema.multiline ? (schema.rows ?? 5) : undefined"
      :autogrow="schema.multiline ? (schema.autogrow ?? false) : undefined"
      :model-value="protectedSecretInputValue"
      :placeholder="protectedSecretPlaceholder"
      :stack-label="Boolean(protectedSecretPlaceholder)"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : t(translationString + '.' + capitalize(props.itemKey))) +
            (isRequiredField ? ' *' : '')
      "
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      v-bind="$attrs"
      @update:model-value="emitProtectedSecretValue"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </q-input>

    <q-input
      v-if="schema.type === 'textarea'"
      ref="formElement"
      :lang="useUserStore().currentRegionalSettings.lang"
      :outlined="schema.outlined ?? true"
      :maxlength="schema.maxLength"
      :dense="schema.dense ?? false"
      :readonly="schema.readonly"
      :hide-bottom-space="true"
      :type="getFieldType(schema.type)"
      :model-value="typeof props.modelValue !== 'undefined' ? props.modelValue : props.schema.value"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      :rows="schema.rows ? schema.rows : 5"
      :autogrow="schema.autogrow ? schema.autogrow : false"
      v-bind="$attrs"
      @update:model-value="emitValue"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </q-input>

    <form v-if="schema.type === 'textarea-rte'" autocorrect="off" autocapitalize="off" autocomplete="off" spellcheck="false">
      <span v-if="!schema.hideLabel" :class="{ 'text-negative': props.formErrors?.$errors?.length > 0 }"
        >{{ ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '') }}
        <slot name="append-label" />
      </span>
      <rich-text-editor
        dialog-icon="palette"
        ref="formElement"
        min-height="5rem"
        outlined
        :toolbar="props.schema.toolbar ? props.schema.toolbar : undefined"
        :definitions="props.schema.definitions ? props.schema.definitions : {}"
        :readonly="schema.readonly"
        :model-value="typeof props.modelValue !== 'undefined' ? (props.modelValue ?? '') : props.schema.value"
        :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
        :error-message="
          getErrorMessage(itemKey, formErrors) ||
          (isRequiredField
            ? $t('Shared.Globals.Errors.Required.default', {
                key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                  ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                  : undefined,
              })
            : null)
        "
        v-bind="$attrs"
        :class="{ 'border-negative-2px': props.formErrors?.$errors?.length > 0 }"
        @update:model-value="emitValue"
      />
      <div v-if="props.formErrors?.$errors?.length > 0" class="q-field__bottom row items-start q-field__bottom--stale q-pt-none text-negative">
        <div class="q-field__messages col">
          <div role="alert">
            {{
              getErrorMessage(itemKey, formErrors) ||
              $t('Shared.Globals.Errors.Required.default', {
                key: translationString + '.' + capitalize(itemKey),
              })
            }}
          </div>
        </div>
      </div>
    </form>

    <q-input
      v-if="schema.type === 'phone'"
      ref="formElement"
      :lang="useUserStore().currentRegionalSettings.lang"
      :outlined="schema.outlined ?? true"
      :dense="schema.dense ?? false"
      :readonly="schema.readonly"
      :hide-bottom-space="true"
      :type="getFieldType(schema.type)"
      :model-value="typeof props.modelValue !== 'undefined' ? props.modelValue : props.schema.value"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      v-bind="$attrs"
      @update:model-value="emitPhoneValue"
    >
      <template v-if="schema.type === 'phone' && countries" #prepend>
        <q-select
          v-model="phoneCountry"
          name="country"
          class="phone-country no-border q-pa-none q-ma-none"
          :hide-bottom-space="true"
          :options="getOptions(itemKey, countries, itemsService, phoneConfig, true)"
          emit-value
          map-options
          options-dense
          @popup-hide="formElement.validate()"
        >
          <template #selected-item="{ opt }">
            <q-icon class="q-pa-md q-mr-sm" :class="opt.avatar"></q-icon>
          </template>
          <template #option="{ itemProps, opt }">
            <q-item v-close-popup v-bind="itemProps">
              <q-item-section avatar>
                <q-icon class="q-pa-md small-text" :class="opt.avatar"></q-icon>
              </q-item-section>
              <q-item-section>
                <q-item-label class="ellipsis">{{ opt.label }} ({{ opt.originalValue.code }})</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </template>
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </q-input>

    <q-input
      v-if="schema.type === 'number'"
      ref="formElement"
      :class="{ 'no-number-arrows': schema.hideArrows }"
      :lang="useUserStore().currentRegionalSettings.lang"
      :outlined="schema.outlined ?? true"
      :dense="schema.dense ?? false"
      :readonly="schema.readonly"
      :prefix="schema.prefix"
      :suffix="schema.suffix"
      :clearable="isClearableField"
      :hide-bottom-space="true"
      :debounce="schema.debounce"
      type="number"
      :pattern="schema.pattern"
      :min="schema.minimum"
      :max="schema.maximum"
      :step="schema.step ?? Math.pow(10, (schema.decimals || 0) * -1)"
      :model-value="props.modelValue ?? props.schema.value ?? null"
      @update:model-value="onModelValueChange"
      :error="props.formErrors?.$errors?.length > 0 || (!props.modelValue && props.modelValue !== 0 && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      v-bind="$attrs"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : t(translationString + '.' + capitalize(props.itemKey))) +
            (isRequiredField ? ' *' : '')
      "
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </q-input>

    <omni-select
      v-if="schema.type === 'option'"
      ref="formElement"
      :lang="useUserStore().currentRegionalSettings.lang"
      :outlined="schema.outlined ?? true"
      :dense="schema.dense ?? false"
      :readonly="schema.readonly"
      :disable="schema.disable || $attrs.disable"
      :searchable-select="schema.searchable"
      :item-key="itemKey"
      :clearable="isClearableField"
      :hide-selected="formElement?.isFocused"
      :hide-bottom-space="true"
      :options="
        getOptions(
          itemKey,
          schema.options,
          itemsService,
          optionsConfig ? optionsConfig : undefined,
          schema.preserveOriginalValues ?? false,
          schema.translateOptions ?? true,
          schema.translationOptionsPrefix ?? undefined,
        )
      "
      :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
      :label="
        showLabel && !schema.hideLabel
          ? ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
          : undefined
      "
      :placeholder="showPlaceholder ? (schema.placeholder ?? $t(translationString + '.' + capitalize(itemKey)) + (isRequiredField ? ' *' : '')) : undefined"
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      :multiple="schema.multiple"
      :emit-value="$attrs.emitValue ? $attrs.emitValue : true"
      map-options
      v-bind="$attrs"
      @field-error="emit('fieldError', props.itemKey)"
      @update:model-value="emitValue"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
      <template v-if="!schema.chips" #selected-item="{ opt, index }">
        <div class="ellipsis">
          <q-icon v-if="opt.avatar" id="country-flag" class="q-pa-md q-mr-sm small-text" :class="opt.avatar" />
          {{ opt.prefix ? opt.prefix + ' - ' : '' }}{{ opt.caption ? opt.caption : opt.label
          }}<span v-if="opt.caption && opt.combineCaptionAndLabel">&nbsp;- {{ opt.label }}</span
          ><span v-if="modelValue?.length && schema.multiple && index !== modelValue.length - 1">,&nbsp;</span>
          <q-tooltip v-if="opt.tooltip" :delay="500" class="line-breaks">{{ opt.tooltip }}</q-tooltip>
        </div>
      </template>
      <template #option="{ itemProps, opt, selected, toggleOption }">
        <q-item v-bind="itemProps" dense>
          <q-item-section v-if="opt.avatar" avatar>
            <q-icon class="q-pa-md small-text" :class="opt.avatar"></q-icon>
          </q-item-section>
          <q-item-section v-if="schema.multiple && (!opt.type || opt.type !== 'header')" side>
            <q-checkbox :model-value="selected" @update:model-value="toggleOption(opt)" />
          </q-item-section>
          <q-item-section>
            <q-item-label v-if="opt.caption" :class="opt.class || undefined"
              ><strong>{{ opt.caption }}</strong></q-item-label
            >
            <q-item-label :class="{ 'text-grey small-text line-breaks': opt.caption }"
              >{{ opt.prefix ? opt.prefix + ' - ' : '' }}{{ opt.label ? opt.label : opt.value[optionsConfig?.labelKey] }}
            </q-item-label>
            <q-tooltip v-if="opt.tooltip" :delay="500" class="line-breaks">{{ opt.tooltip }}</q-tooltip>
          </q-item-section>
          <q-item-section v-if="opt.prepend" side>
            <span class="text-uppercase text-caption"> {{ opt.prepend }}</span>
          </q-item-section>
        </q-item>
      </template>
    </omni-select>

    <template v-if="schema.type === 'radio' || schema.type === 'checkbox'">
      <span v-if="!schema.hideLabel" :class="{ 'text-negative': props.formErrors?.$errors?.length > 0 }"
        >{{ ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '') }}
        <slot name="append-label" />
      </span>
      <q-option-group
        ref="formElement"
        :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
        :options="
          getOptions(
            itemKey,
            schema.options,
            itemsService,
            optionsConfig ? optionsConfig : undefined,
            schema.preserveOriginalValues ?? false,
            schema.translateOptions ?? true,
            schema.translationOptionsPrefix ?? undefined,
          )
        "
        :disable="schema.readonly || $attrs.readonly"
        :type="schema.type || 'radio'"
        :inline="schema.inline"
        v-bind="$attrs"
        @update:model-value="emitValue"
      />
      <div v-if="props.formErrors?.$errors?.length > 0" class="q-field__bottom q-pl-none row items-start q-field__bottom--stale q-pt-none text-negative">
        <div class="q-field__messages col">
          <div role="alert">
            {{
              getErrorMessage(itemKey, formErrors) ||
              $t('Shared.Globals.Errors.Required.default', {
                key: translationString + '.' + capitalize(itemKey),
              })
            }}
          </div>
        </div>
      </div>
    </template>

    <template v-if="schema.type === 'stars'">
      <span v-if="!schema.hideLabel" class="block q-mb-xs" :class="{ 'text-negative': props.formErrors?.$errors?.length > 0 }">
        {{ ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '') }}
        <slot name="append-label" />
      </span>
      <q-btn
        v-for="n in stars"
        :key="n"
        flat
        round
        size="18px"
        :disable="schema.readonly || $attrs.readonly"
        :icon="effectiveRating >= n ? 'star' : 'star_border'"
        :color="effectiveRating >= n ? 'primary' : props.formErrors?.$errors?.length > 0 && effectiveRating === 0 ? 'negative' : 'black'"
        @mouseenter="setHoverRating(n)"
        @mouseleave="clearHoverRating"
        @click="setRating(n)"
      />
      <div v-if="props.formErrors?.$errors?.length > 0" class="q-field__bottom q-pl-none row items-start q-field__bottom--stale q-pt-none text-negative">
        <div class="q-field__messages col">
          <div role="alert">
            {{
              getErrorMessage(itemKey, formErrors) ||
              $t('Shared.Globals.Errors.Required.default', {
                key: translationString + '.' + capitalize(itemKey),
              })
            }}
          </div>
        </div>
      </div>
    </template>

    <label-select
      v-if="schema.type === 'array-strings'"
      ref="formElement"
      :value-type="schema.valueType"
      :readonly="schema.readonly"
      :options="schema.options"
      :use-chips="schema.useChips"
      :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
      :label="
        showLabel && !schema.hideLabel
          ? ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
          : undefined
      "
      :placeholder="showPlaceholder ? $t(translationString + '.' + capitalize(itemKey)) + (isRequiredField ? ' *' : '') : undefined"
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      @update:model-value="emitValue"
      v-bind="$attrs"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </label-select>

    <q-item
      v-if="schema.type === 'boolean'"
      :disable="schema.readonly || $attrs.readonly"
      id="toggle"
      tag="label"
      top
      class="rounded-borders bordered q-field__control q-px-none"
      :class="{ 'hide-bottom-margin': schema.hideBottomMargin }"
      v-bind="$attrs"
    >
      <q-item-section v-if="!schema.hideLabel">
        <q-item-label :class="`q-field__label${$attrs.dense ? '--dense' : ''} padding-12`"
          >{{
            ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
          }}
        </q-item-label>
      </q-item-section>
      <q-item-section avatar>
        <q-toggle
          ref="formElement"
          :dense="$attrs.dense"
          :disable="schema.readonly || $attrs.readonly"
          :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
          :hide-bottom-space="true"
          :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
          :error-message="
            getErrorMessage(itemKey, formErrors) ||
            (isRequiredField
              ? $t('Shared.Globals.Errors.Required.default', {
                  key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                    ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                    : undefined,
                })
              : null)
          "
          @update:model-value="emitValue"
          v-bind="$attrs"
        >
        </q-toggle>
      </q-item-section>
      <q-item-section side class="mr-12 pl-4" v-if="$slots['append']">
        <slot name="append" />
      </q-item-section>
    </q-item>

    <q-checkbox
      v-if="schema.type === 'boolean-small'"
      ref="formElement"
      :lang="useUserStore().currentRegionalSettings.lang"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      v-bind="$attrs"
      :disable="schema.readonly"
      :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
      :hide-bottom-space="true"
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      @update:model-value="emitValue"
    >
    </q-checkbox>

    <date-picker
      v-if="schema.type === 'date' || schema.type === 'date-time' || schema.type === 'time'"
      ref="formElement"
      :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
      :options="schema.options"
      :min="schema.min"
      :max="schema.max"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        formElement?.datePickerErrorMessage ||
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      :readonly="schema.readonly"
      :clearable="schema.clearable"
      :emit-null-on-empty-input="schema.emitNullOnEmptyInput"
      :mode="schema.type"
      v-bind="$attrs"
      @update:model-value="emitValue"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </date-picker>

    <color-picker
      dialog-icon="palette"
      v-if="schema.type === 'color'"
      ref="formElement"
      :auto-update="schema.autoUpdate"
      :format-model="schema.formatModel"
      :model-value="modelValue ?? schema.value"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        formElement?.datePickerErrorMessage ||
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      :hide-title-bar="schema.hideTitleBar"
      :persistent="schema.persistent"
      :readonly="schema.readonly"
      :show-buttons="schema.showButtons"
      v-bind="$attrs"
      @update:model-value="emitValue"
    />

    <omni-slider
      v-else-if="schema.type === 'slider'"
      ref="formElement"
      :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
      :label="
        schema.hideLabel
          ? ''
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      :pop-up-label="schema.popUpLabel"
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      :readonly="schema.readonly"
      :disable="schema.disable"
      :range="schema.range"
      :min="schema.minimum"
      :max="schema.maximum"
      :step="schema.step"
      :dense="schema.dense"
      :snaps="schema.snaps"
      :markers="schema.markers"
      :marker-labels="schema.markerLabels"
      :marker-labels-class="schema.markerLabelsClass"
      :vertical="schema.vertical"
      :color="schema.color"
      :track-color="schema.trackColor"
      :thumb-color="schema.thumbColor"
      :label-always="schema.labelAlways"
      :prefix="schema.prefix"
      :suffix="schema.suffix"
      v-bind="$attrs"
      @update:model-value="emitValue"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </omni-slider>

    <template v-else-if="schema.type === 'json'">
      <span v-if="!schema.hideLabel" :class="{ 'text-negative': props.formErrors?.$errors?.length > 0 }"
        >{{ ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '') }}
        <slot name="append-label" />
      </span>
      <omni-json-editor
        :model-value="typeof modelValue !== 'undefined' ? modelValue : schema.value"
        @update:model-value="emitValue"
        :read-only="schema.readonly"
      />
      <div v-if="props.formErrors?.$errors?.length > 0" class="q-field__bottom row items-start q-field__bottom--stale q-pt-none text-negative">
        <div class="q-field__messages col">
          <div role="alert">
            {{
              getErrorMessage(itemKey, formErrors) ||
              $t('Shared.Globals.Errors.Required.default', {
                key: translationString + '.' + capitalize(itemKey),
              })
            }}
          </div>
        </div>
      </div>
    </template>

    <file-uploader
      v-else-if="schema.type === 'files'"
      ref="formElement"
      :model-value="modelValue"
      :label="
        schema.hideLabel
          ? undefined
          : ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '')
      "
      :object-reference="schema.objectReference"
      :accept="schema.accept"
      :clearable="schema.clearable"
      :max-file-size="schema.maxFileSize"
      :max-total-size="schema.maxTotalSize"
      :multiple="schema.multiple"
      :readonly="schema.readonly || $attrs.readonly"
      :disable="schema.disable || $attrs.disable"
      :show-save-button="schema.showSaveButton"
      :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
      :error-message="
        getErrorMessage(itemKey, formErrors) ||
        (isRequiredField
          ? $t('Shared.Globals.Errors.Required.default', {
              key: te(translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey)))
                ? translationString + '.' + (schema.translationKey ? schema.translationKey : capitalize(itemKey))
                : undefined,
            })
          : null)
      "
      v-bind="$attrs"
      @update:model-value="emitValue"
    >
      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" v-bind="slotData || {}"></slot>
      </template>
    </file-uploader>

    <template v-if="schema.type === 'code'">
      <span v-if="!schema.hideLabel" :class="{ 'text-negative': props.formErrors?.$errors?.length > 0 }"
        >{{ ($attrs.label || schema.label ? $attrs.label || schema.label : $t(translationString + '.' + capitalize(itemKey))) + (isRequiredField ? ' *' : '') }}
        <slot name="append-label" />
      </span>
      <code-editor
        ref="formElement"
        :model-value="typeof props.modelValue !== 'undefined' ? (props.modelValue ?? '') : props.schema.value"
        :read-only="schema.readonly"
        :error="props.formErrors?.$errors?.length > 0 || (!modelValue && parentHasError && isRequiredField)"
        :editor-class="['omni-code-editor']"
        :show-language-select="schema.showLanguageSelect"
        :default-language="schema.defaultLanguage"
        v-bind="$attrs"
        @update:model-value="emitValue"
      />
      <div v-if="props.formErrors?.$errors?.length > 0" class="q-field__bottom row items-start q-field__bottom--stale q-pt-none text-negative">
        <div class="q-field__messages col">
          <div role="alert">
            {{
              getErrorMessage(itemKey, formErrors) ||
              $t('Shared.Globals.Errors.Required.default', {
                key: translationString + '.' + capitalize(itemKey),
              })
            }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped></style>
